<template>
  <ion-page id="legacy-rep-practice-page">
    <ion-content>

      <div v-show="showTour">
        <Tour  :tour="tourInfo" :tourTrigger="tourTrigger" @tour-done="tourDone" />
      </div>

      <div id="the_board" :style="styleBoard">
        <CMChessboard :fen="fen" :validMoves="availableMoves" :orientation="orientation" 
                  :animateOnePly="animateFen" :hint="hintFen"
                  :animateSequence="revealFenArray" @move-animated="moveAnimated"
                  :boardSize="boardSize" @board-move="boardMove" />
      </div>

      <div id="the_status" :style="styleStatus" :class="{ 'text-dark' : isDark,'text-light' : !isDark }">
        {{ statusText }}
      </div>

      <div id="the_movehistory" :style="styleMoveHistory" :class="{ 'text-dark' : isDark,'text-light' : !isDark }">
        {{ initialRepertoireName}}
        <MoveHistory :moveHistoryStack="moveHistoryStack" :trigger="moveHistoryTrigger" />
      </div>

      <div id="the_tree" v-show="showTree != 'hide'" :style="styleRepTree" :class="{ 'text-dark' : isDark,'text-light' : !isDark }">
        <LegacyRepTree calledFrom="practice" :showComments="showComments" :fenidPath="repTreeFenidPath" :reloadRepertoire="reloadTrigger" />
      </div>

      <ActionSheet heading="Tree Display" :items="actionSheetButtons" 
                    :trigger="actionSheetTrigger"
                    @action-sheet-result="actionSheetResult">
      </ActionSheet> 
    </ion-content>
    <Toolbar id="the_toolbar" :elements="toolbarElements" :ignoreClicks="showTour" 
                    :trigger="toolbarTrigger" @toolbar-click="toolbarClick" />
  </ion-page>
</template>

<script setup>

  import { IonItem, IonList, IonButton, IonRange,IonPage, IonContent, modalController } from '@ionic/vue';

  import ActionSheet from '@/components/ActionSheet.vue'
  import ModalLegacyRepSelector from '@/components/ModalLegacyRepSelector.vue'

  import { findToolbarElement, setToolbarElementDisabledFlag, setToolbarElementLabel} 
                from '@/composables/toolbarSupport.js'

  import CMChessboard from '@/components/CMChessboard.vue'

  import Toolbar from '@/components/Toolbar.vue'
  import MoveHistory from '@/components/MoveHistory.vue'
  import LegacyRepTree from '@/components/LegacyRepTree.vue'
  import ModalErrorMessage from '@/components/ModalErrorMessage.vue'
  import { t , replaceMultipleAll } from '@/composables/translateText.js'

  import { ref, watch } from 'vue'
  import { onMounted, onBeforeMount } from 'vue'
  import { useGetData } from '@/composables/getData.js'
  import { useConnectionStore } from '@/stores/connectionStore';
  import { useStringStore } from '@/stores/stringStore';

  import { doStartupCheck } from '@/composables/startupCheck.js'
  import { availableWidthHeight, calculateChessBoardRect, isScreenPortraitOrientation } from '@/composables/screenSize'
  import { useWindowSizeStore } from '@/stores/windowSizeStore';
  import Tour from '@/components/Tour.vue'


  const stringStore = useStringStore()

  const connectionStore = useConnectionStore()
  
  const isDark = ref(connectionStore.getDarkmode != "light")
  const menuTrigger = ref(0)
  const tourTrigger = ref(0)
  const tourOption = connectionStore.getTourOption
  const showTour = ref((tourOption == "show"))
  
  const showComments = ref(true)

  const TOOLBAR_HINT = 0
  const TOOLBAR_REVEAL = 1
  const TOOLBAR_AGAIN = 2
  const TOOLBAR_NEXT = 3
  const TOOLBAR_TREE = 4
  const TOOLBAR_REPERTOIRES = 5

  const INITIAL_FEN = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"

  const MOVEHISTORY_FONTSIZE = 16
  const MOVEHISTORY_HEIGHT =  MOVEHISTORY_FONTSIZE * 1.2 * 2 + 4

  const toolbarElements = ref([
    { index : TOOLBAR_HINT, label : t("Hint") ,disabled : false,collapse:false},
    { index : TOOLBAR_REVEAL, label : t("Reveal") , disabled : true,collapse:false},
    { index : TOOLBAR_AGAIN, label : t("Again") , disabled : true,collapse:true},
    { index : TOOLBAR_NEXT, label : t("Next") , disabled : true,collapse:false},
    { index : TOOLBAR_TREE, label : t("Tree") , disabled : false, collapse:false,hasAction:true},
    { index : TOOLBAR_REPERTOIRES, label : t("Repertoires") , disabled : false, collapse:true},
  ])

  // Tree Display Action sheet

  const ACTION_NO_TREE = 10
  const ACTION_STATIC_TREE = 11
  const ACTION_DYNAMIC_TREE = 12
  const ACTION_COMMENT_TREE = 13

  const actionSheetTrigger = ref(0)
  const actionSheetButtons = [
        { text: t("Hide"), data: { index : ACTION_NO_TREE } },
        { text: t("Display"), data: { index : ACTION_STATIC_TREE } },
        { text: t("Real-Time"), data: { index : ACTION_DYNAMIC_TREE } },
        { text: t('Cancel'), role: 'cancel',data: { index : -1}},
    ];

  const windowSizeStore = useWindowSizeStore()
  const windowSizeRef = ref({ width : windowSizeStore.getWindowSize['width'],
                        height : windowSizeStore.getWindowSize['height'] })
  const boardSize = ref(100)
  const reloadTrigger = ref(0)

  var blankAppendTrigger = " "
  var fenid = "1"
  var playingWhite = 1
  var forceOnlyCurrentLine = 0
  var orientation = ref("white")
  var repTreeSlotId = ref("practice")
  const animateFen = ref([])
  const hintFen = ref([])

  var isPortrait = false

  const fen = ref(INITIAL_FEN)
  const availableMoves = ref([])

  const moveHistoryStack = ref([])
  const repTreeFenidPath = ref("")
  const moveHistoryTrigger = ref(0)
  const revealMoveStack = ref([])
  const revealFenArray = ref([])
  const toolbarTrigger = ref(0)
  const statusText = ref("Repertoire Name")
  const initialRepertoireName = ref("Retrieving the Repertoire Information")

  var repertoireName = ""
  var possibleLineCount = 0
  var currentLineNumber = 0
  var endOfLineReached = false
  var hintIndex = 0
  var forceOnlyCurrentLine = 0

  // tour stuff
  const tourInfo = ref({
    id : "repertoire_practice",
    video : "https://www.chessvis.com/videos/repertoires_on_web.mp4",
    elements : [
      { id: "", title: t("Repertoire Practice"), text: t("What good is defining a repertoire if you can't learn it? Can't practice it?<br><br><i>Note: the video is for the entire set of repertoire routines.</i>")},
      { id: "", title: t("Not 'Drill and Kill'"), text: t("I've been in many plays where I've had parts with hundreds of lines to deliver on stage from memory. The trick is to <i>learn</i> the lines, not <i>memorize</i> them. Internalize them.<br><br>Early rehersals start with <i>script in hand</i>. Why should learning a repertoire be any different?")},
      { id: "", title: t("Follow Along Tree View"), text: t("The <i>tree</i> is your <i>script</i>.<br><br>With Chessvis, you can display the tree while you practice.")},
      { id: "", title: t("Build Specialized Repertoires"), text: t("Repertoires are best built as focused answers to a particular set of opponent moves.<br><br>Maybe you have a repertoire that answers the Sicilian, another for the French, Caro-Kahn and so on.")},
      { id: "", title: t("Practice Together"), text: t("Chessvis can bring any number of repertoires together so you can practice them as though they one big repertoire.")},
      { id: "the_status", title: t("Randoming the Lines"), text: t("Before starting, Chessvis builds all the different 'lines'.<br><br>After each of your moves, if there are multiple opponent moves, you will experience them in random order.")},
      { id: "the_toolbar", title: t("Hint - Toolbar"), text: t("'Hint' is always here to help you remember what move comes next in your repertoire. And if there are multiple choices left, it will step through those.")},
      { id: "the_toolbar", title: t("Reveal - Toolbar"), text: t("Plays all the moves in the current line.<br><br>Note: this option is only available after the current line is played far enough to be uniquely identified.")},
      { id: "the_toolbar", title: t("Again - Toolbar"), text: t("Want to run this line again? This option resets to be beginning of the current line.<br><br>Note: this option is only available after the current line is played far enough to be uniquely identified.")},
      { id: "the_toolbar", title: t("Next - Toolbar"), text: t("Brings up the next random line.<br><br>Note: this option is only available after the current line is played far enough to be uniquely identified.")},
      { id: "the_toolbar", title: t("Tree - Toolbar"), text: t("This is the equivalent of learning your lines with '<i>script in hand</i>'.<br><br>You have a choice between a static display of the tree or a '<i>real-time</i>' tree that keeps up with your moves.")},
      { id: "the_toolbar", title: t("Repertoire - Toolbar"), text: t("Select any number of repertoires to practice. If you select more than one, they will be merged together for you to practice as a whole.<br><br>Note: practice is one color at a time.")},
    ]
  })

  const styleBoard = ref({
    position : "absolute",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px"
  })

  const styleMoveHistory = ref({
    position : "absolute",
    "overflow-y" : "scroll",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px",
    "font-size" : MOVEHISTORY_FONTSIZE + "px",
    "text-align" : "center",
    "line-height" : "1.2em"
  })

  const styleStatus = ref({
    position : "absolute",
    "overflow-y" : "scroll",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px",
    "font-size" : MOVEHISTORY_FONTSIZE + "px",
    "text-align" : "center",
    "line-height" : "1.2em"
  })

  const styleRepTree = ref({
    position : "absolute",
    "overflow-y" : "scroll",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px"
  })

  const showTree = ref("hide")
  
  doStartupCheck(false,afterStartupCallbackRoutine)

  function afterStartupCallbackRoutine(stuffChanged) {

   // this can be used to switch away from non-subscribers
   if (connectionStore.getIsSubscriber != 1) {
      //console.log("################### subscriber value",connectionStore.getIsSubscriber)
      return
    }

    updateWidthHeights()
    buildPracticeLines()
    menuTrigger.value++
  }
 
  function tourDone() {
    showTour.value = false
  }

  function repertoiresDone(didChange) {
    //console.log("repertoires change?  and show status",didChange,showTree.value)

    if (didChange) {
      reloadTrigger.value++
      buildPracticeLines()
    }
  }

  function moveAnimated(arrayIndex) {
    //console.log("move animated ",arrayIndex)
    moveHistoryStack.value.push(revealMoveStack.value[arrayIndex])
    moveHistoryTrigger.value++

    buildRepTreeFenidPath()

    if (arrayIndex == revealMoveStack.value.length - 1) {
      setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_REVEAL,false)
      setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_AGAIN,false)
      setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_NEXT,false)
      toolbarTrigger.value++
    }
  }

  function buildRepTreeFenidPath() {
    if (showTree.value != "dynamic")
      return;

    var fenidArray = []

    for (var i=0; i < moveHistoryStack.value.length; i++)
      fenidArray.push(moveHistoryStack.value[i].fenid)
    repTreeFenidPath.value = fenidArray.join(",")

    //console.log("reptree path  ",repTreeFenidPath.value)
  }

  function boardMove(arrayIndex,from,to) {
    //console.log("*** board move",arrayIndex,from,to)
    initialRepertoireName.value = ""

    moveHistoryStack.value.push(availableMoves.value[arrayIndex])
    moveHistoryTrigger.value++
    buildRepTreeFenidPath()

    // fenid list
    var fenids = []
    for (var i=0; i < moveHistoryStack.value.length; i++)
      fenids.push(moveHistoryStack.value[i].fenid)
    const fenidPath = fenids.join(",")

    getPracticeMove(fenidPath, playingWhite, fenids.length, 
                      0, 0, forceOnlyCurrentLine, currentLineNumber)
  }

  function updateStyleObjects(boundaries) {
    boardSize.value = boundaries['board'].height

    styleBoard.value.height = boundaries['board'].height + "px"
    styleBoard.value.width = boundaries['board'].width + "px"
    styleBoard.value.top = boundaries['board'].top + "px"
    styleBoard.value.left = boundaries['board'].left + "px"

    styleMoveHistory.value.height = boundaries['history'].height + "px"
    styleMoveHistory.value.width = boundaries['history'].width + "px"
    styleMoveHistory.value.top = boundaries['history'].top + "px"
    styleMoveHistory.value.left = boundaries['history'].left + "px"

    styleStatus.value.height = boundaries['status'].height + "px"
    styleStatus.value.width = boundaries['status'].width + "px"
    styleStatus.value.top = boundaries['status'].top + "px"
    styleStatus.value.left = boundaries['status'].left + "px"

    styleRepTree.value.height = boundaries['tree'].height + "px"
    styleRepTree.value.width = boundaries['tree'].width + "px"
    styleRepTree.value.top = boundaries['tree'].top + "px"
    styleRepTree.value.left = boundaries['tree'].left + "px"

    //console.log("**** boardsize ",boardSize.value)
  }

  function updateWidthHeights() {

    const availableSpace = availableWidthHeight()
    isPortrait = isScreenPortraitOrientation(availableSpace)
    const boundaries = (isPortrait) ? calculatePortraitWidthHeights(availableSpace) :
                                      calculateLandscapeWidthHeights(availableSpace)
    
    updateStyleObjects(boundaries)
}

function calculatePortraitWidthHeights(availableSpace) {

  // reserve space for the practice lines

  const availableTreeHeight = (showTree.value != "hide") ? availableSpace.height * .25 : 0

  const availableSpaceForBoard = { width : availableSpace.width ,
                height : availableSpace.height - MOVEHISTORY_HEIGHT - MOVEHISTORY_HEIGHT 
                        - availableTreeHeight,
                isPortrait : true }

  const boardDimensions = calculateChessBoardRect(availableSpaceForBoard,1.0)

  const boardHeight = boardDimensions.height
  const boardWidth = boardDimensions.width
  const boardPositionTop = boardDimensions.y
  const boardPositionLeft = boardDimensions.x + availableSpace.leftRightIndent

  const statusPositionTop = boardPositionTop + boardHeight
  const statusPositionLeft = availableSpace.leftRightIndent
  const statusHeight = MOVEHISTORY_HEIGHT
  const statusWidth = availableSpace.width - availableSpace.leftRightIndent * 2

  const moveHistoryPositionTop = statusPositionTop + statusHeight
  const moveHistoryPositionLeft = statusPositionLeft
  const moveHistoryHeight = MOVEHISTORY_HEIGHT
  const moveHistoryWidth = statusWidth

  const treePositionTop = moveHistoryPositionTop + moveHistoryHeight
  const treePositionLeft = moveHistoryPositionLeft
  const treeHeight = availableTreeHeight
  const treeWidth = statusWidth

  const result = { board : {
      width : boardWidth,
      height : boardHeight,
      left : boardPositionLeft + availableSpace.leftIndent,
      top : boardPositionTop + availableSpace.topIndent
    },
    history : {
      width : moveHistoryWidth,
      height : moveHistoryHeight,
      left : moveHistoryPositionLeft + availableSpace.leftIndent,
      top : moveHistoryPositionTop + availableSpace.topIndent
    },
    status : {
      width : statusWidth,
      height : statusHeight,
      left : statusPositionLeft + availableSpace.leftIndent,
      top : statusPositionTop + availableSpace.topIndent
    },
    tree : {
      width : treeWidth,
      height : treeHeight,
      left : treePositionLeft + availableSpace.leftIndent,
      top : treePositionTop + availableSpace.topIndent
    },
  }
  //console.log("Portrait board and tree dimensions",result)

  return (result)
}

function calculateLandscapeWidthHeights(availableSpace) {

  // reserve space for the instruction lines
  const availableSpaceForBoard = { width : availableSpace.width - 150,
                height : availableSpace.height ,
                isPortrait : false }
  const boardDimensions = calculateChessBoardRect(availableSpaceForBoard,1.0)

  const boardHeight = boardDimensions.height
  const boardWidth = boardDimensions.width
  const boardPositionTop = boardDimensions.y
  const boardPositionLeft = boardDimensions.x + availableSpace.leftRightIndent

  const statusPositionTop = 0
  const statusPositionLeft = boardPositionLeft + boardWidth + availableSpace.leftRightIndent
  const statusHeight = MOVEHISTORY_HEIGHT
  const statusWidth = availableSpace.width - statusPositionLeft

  const moveHistoryPositionTop = statusPositionTop + statusHeight
  const moveHistoryPositionLeft = statusPositionLeft
  const moveHistoryHeight = MOVEHISTORY_HEIGHT * 2
  const moveHistoryWidth = statusWidth

  const treePositionTop = moveHistoryPositionTop + moveHistoryHeight
  const treePositionLeft = moveHistoryPositionLeft
  const treeHeight =  availableSpace.height - treePositionTop
  const treeWidth = moveHistoryWidth

  const result = { 
    board : {
      width : boardWidth,
      height : boardHeight,
      left : boardPositionLeft + availableSpace.leftIndent,
      top : boardPositionTop + availableSpace.topIndent
    },
    history : {
      width : moveHistoryWidth,
      height : moveHistoryHeight,
      left : moveHistoryPositionLeft + availableSpace.leftIndent,
      top : moveHistoryPositionTop + availableSpace.topIndent
    },
    status : {
      width : statusWidth,
      height : statusHeight,
      left : statusPositionLeft + availableSpace.leftIndent,
      top : statusPositionTop + availableSpace.topIndent
    },
    tree : {
      width : treeWidth,
      height : treeHeight,
      left : treePositionLeft + availableSpace.leftIndent,
      top : treePositionTop + availableSpace.topIndent
    }
  }
  return (result)
}

  watch(() => windowSizeStore.getWindowSize, () => {
      //console.log('*********** Explorer -- window size changes detected')
      windowSizeRef.value = { width : windowSizeStore.getWindowSize['width'],
                        height : windowSizeStore.getWindowSize['height'] }
      //console.log("*** Explorer new window size",windowSizeRef.value)
      updateWidthHeights()
      menuTrigger.value++
    })

  const openErrorModal = async (programName,errorMessage) => {
    const modal = await modalController.create({
      component: ModalErrorMessage,
      componentProps: {
        type : "commError",
        program : programName ,
        errorMessage : errorMessage
      }
    });

    modal.present();

    const { data, role } = await modal.onWillDismiss();
  };

  // Repertoire Selector Modal
  const repertoireSelectorModal = async () => {
    const modal = await modalController.create({
      component: ModalLegacyRepSelector,
      componentProps: {
        descriptionHeading : t('Select the Repertoires and color you want to practice. Multiple repertoires can be merged. One color at a time') ,
        whoCalled : "practice"
      }
    });

    modal.present();

    const { data, role } = await modal.onWillDismiss();

    if (role === 'confirm') {
      repertoiresDone(true)
    }
  };

  function actionSheetResult(action,index) {
    if (action != "yes")
      return
    actionClick(index)
  }

  function actionClick(index) {
    switch (index) {
      case ACTION_COMMENT_TREE :
        showComments.value = !showComments.value
        // setToolbarElementLabel(toolbarElements.value,TOOLBAR_COMMENT_TREE,
        //                       (showComments.value) ? "Hide Comments" : "Show Comments")
        break
      case ACTION_NO_TREE :
        showTree.value = "hide"
        if (isPortrait)
          updateWidthHeights()
        break;
      case ACTION_STATIC_TREE :
        showTree.value = "static"
        if (isPortrait)
          updateWidthHeights()
        break;
      case ACTION_DYNAMIC_TREE :
        showTree.value = "dynamic"
        buildRepTreeFenidPath()
        if (isPortrait)
          updateWidthHeights()
        break;
    }
  }

  function toolbarClick(index) {

    // trap that the button should be disabled but the screen hasn't caught up
    var element = findToolbarElement(toolbarElements.value,index)
    if (element != null && element.disabled)
      return


    switch (index) {
      case TOOLBAR_TREE :
        actionSheetTrigger.value++
        break
      case TOOLBAR_HINT :
        if (availableMoves.value.length == 0) {
          setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_HINT,true)
          toolbarTrigger.value++
          break;
        }
        var currentFen
        if (moveHistoryStack.value.length)
          currentFen = moveHistoryStack.value[moveHistoryStack.value.length-1].fen
        else
          currentFen = INITIAL_FEN
        hintFen.value = [currentFen, availableMoves.value[hintIndex].fen]
        //animateFen.value = [fen.value, availableMoves.value[hintIndex].fen]
        hintIndex++
        if (hintIndex == availableMoves.value.length)
          hintIndex = 0
        break;
      case TOOLBAR_REVEAL :
        setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_HINT,true)
        setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_REVEAL,true)
        setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_AGAIN,true)
        setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_NEXT,true)
        toolbarTrigger.value++

        incrementAppendTrigger()
        fen.value = INITIAL_FEN + blankAppendTrigger
        getReveal(currentLineNumber)
        break;
      case TOOLBAR_AGAIN :
        setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_REVEAL,true)
        setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_AGAIN,true)
        setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_NEXT,true)

        toolbarTrigger.value++
        resetBoard()
        forceOnlyCurrentLine = 1
        getPracticeMove("", playingWhite, 0, 0, 0, forceOnlyCurrentLine, currentLineNumber)
        break;
      case TOOLBAR_NEXT :
        setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_REVEAL,true)
        setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_AGAIN,true)
        setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_NEXT,true)
        toolbarTrigger.value++
        resetBoard()
        forceOnlyCurrentLine = 0
        getPracticeMove("", playingWhite, 0, 0, 1, forceOnlyCurrentLine, currentLineNumber)
        break
      case TOOLBAR_REPERTOIRES :
        if (moveHistoryStack.value.length)
          fen.value = moveHistoryStack.value[moveHistoryStack.value.length-1].fen
        else
          fen.value = INITIAL_FEN
        repertoireSelectorModal()
        break;
    }
  }

  function resetBoard() {
    //console.log("reset board")
    moveHistoryStack.value = []
    moveHistoryTrigger.value++
    incrementAppendTrigger()
    fen.value = INITIAL_FEN + blankAppendTrigger
    buildRepTreeFenidPath()
  }

  function incrementAppendTrigger() {
    if (blankAppendTrigger.length > 64)
      blankAppendTrigger = ""
    blankAppendTrigger += " "
  }

  function processBuildPracticeLinesJsonResponse(json) {
    //console.log(" in callback",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }

    resetBoard()

    repertoireName = json['name']
    if (repertoireName.length == 0) {
      initialRepertoireName.value = t("No Repertoire Selected")
      return
    }
    initialRepertoireName.value = repertoireName.replace("~server_merged~",t("Merged"))
    playingWhite = (json['playing'] == 1) ? 1 : 0

    incrementAppendTrigger()
    orientation.value = (playingWhite == 1) ? "white" + blankAppendTrigger : "black" + blankAppendTrigger

    //console.log("in build process  ",playingWhite)

    getPracticeMove("", playingWhite, moveHistoryStack.value.length, 0, 0, 0, 0)
  }

  function buildPracticeLines() {
    //console.log("*** get build practice lines")
    var formData = {
    }
    useGetData("chessvis_legacy_repertoire_practice_lines.php", formData, processBuildPracticeLinesJsonResponse)
  }


  function processPracticeMoveJsonResponse(json) {

    //console.log(" in practice move callback",json)

    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }

    possibleLineCount =parseInt(json['l_count']);

    // can't reveal, do again or go to the next until the tree is down to one branch

    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_REVEAL,(possibleLineCount >= 2))
    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_AGAIN,(possibleLineCount >= 2))
    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_NEXT,(possibleLineCount >= 2))

    toolbarTrigger.value++

    if (possibleLineCount <= 1) {
        const possibleLineNumber = parseInt(json['l_number']);

        if (possibleLineNumber >= 0)
            currentLineNumber = possibleLineNumber;
    }

    //console.log("**** CURRENT LINE NUMBER  ",currentLineNumber)

    if (possibleLineCount == 0) {
        statusText.value = "******** " + t("End of Line") + " ********"
        endOfLineReached = true;
        setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_HINT,true)
        toolbarTrigger.value++
        return;
    }
    else {
        endOfLineReached = false;
        setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_HINT,false)
        toolbarTrigger.value++
    }

    if (moveHistoryStack.value.length == 0) {
        const serverTranslations = [
          [ "~server_of_lines_done~", t("of")],
          ["~server_lines_done~", t("lines done") ]
        ]
        statusText.value = replaceMultipleAll(json["display_string"],serverTranslations)
    }

    const opponentFenid = json["o_fenid"];

    if (opponentFenid == 1)
      resetBoard()
    else {
      const opponentMove = {
        fenid : opponentFenid,
        fen : json['o_fen'],
        san : json['o_san'],
        from : json['o_from'],
        to : json['o_to']
      }
      
      const currentFen = (moveHistoryStack.value.length) ? 
              moveHistoryStack.value[moveHistoryStack.value.length-1].fen : INITIAL_FEN
      animateFen.value = [currentFen, json['o_fen']]
      moveHistoryStack.value.push(opponentMove)
      moveHistoryTrigger.value++
      buildRepTreeFenidPath()
    }
    
    availableMoves.value = []
    const playerFenids = json["p_fenids"].split(",")
    const playerFens = json["p_fens"].split(",")
    const playerFroms = json["p_froms"].split(",")
    const playerTos = json["p_tos"].split(",")
    const playerSans = json["p_sans"].split(",")
    const playerCount = json["p_count"]

    for (var i=0; i < playerCount; i++) {
      const oneMove = {
        fenid: playerFenids[i],
        fen : playerFens[i],
        san : playerSans[i],
        from : playerFroms[i],
        to : playerTos[i]
      }
      availableMoves.value.push(oneMove)
    }

      // reset the index
      hintIndex = 0;
  }

  function getPracticeMove(fenidPath,playingWhite,ply,doReset,doNext,forceAgain,lineNumber) {
    //console.log("*** get practice move")
    var formData = {
      fenid_path : fenidPath,
      white : playingWhite,
      ply : ply,
      do_reset : doReset,
      do_next : doNext,
      force_again : forceAgain,
      c_linenumber : lineNumber
    }
    //console.log("practice move formdata",formData)
    useGetData(" chessvis_legacy_repertoire_practice_move.php", formData, processPracticeMoveJsonResponse)
  }

  function processRevealJsonResponse(json) {
    //console.log(" in reveal callback",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }

    availableMoves.value = []

    revealMoveStack.value = []
    revealFenArray.value = [INITIAL_FEN]
    const fenids = json["fenid"].split(",")
    const fens = json["fen"].split(",")
    const froms = json["from"].split(",")
    const tos = json["to"].split(",")
    const sans = json["san"].split(",")
    const count = json["count"]

    for (var i=0; i < count; i++) {
      const oneMove = {
        fenid: fenids[i],
        fen : fens[i],
        san : sans[i],
        from : froms[i],
        to : tos[i]
      }
      revealMoveStack.value.push(oneMove)
      revealFenArray.value.push(fens[i])
    }
    //console.log("reveal Fen Array",revealFenArray.value)

    // move history stack will get populated as the reveal takes place
    moveHistoryStack.value = []
    moveHistoryTrigger.value++
    resetBoard()
  }

  function getReveal(lineNumber) {
    //console.log("*** get reveal")
    var formData = {
      c_linenumber : lineNumber
    }
    //console.log("reveal formdata",formData)
    useGetData("chessvis_legacy_repertoire_practice_reveal.php", formData, processRevealJsonResponse)
  }
</script>

<style scoped>

#practice-container {
  position : relative;
}

</style>